<script setup>
import router, { menuRouteEnum, menuRoutes } from '@/router'
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { useGridRef, useODataSource, useDefaultGridOptions, useUpdateMasterDetail, useDefaultExport, useRouteColumnFilter, refreshGridAsync, getCreatedRowId, refreshChangedRowAsync } from '@/composables/data-grid'
import { entityChangeTypeEnum } from '@/stores/portal.store'
import { useEntityChanges } from '@/composables/entity-changes'
import * as dataGridColumn from '@/composables/data-grid-column'
import { useNotify } from '@/composables/notify'
import { useArchive } from '@/composables/archive'
import { useQuoteDeleteOptions } from '@/composables/delete-options'
import { useDataGridWidget } from '@/composables/data-grid-toolbar'
import { useChips } from '@/composables/chips'
import { useDetectDevice } from '@/composables/device'

import ArchiveGridButton from '@/components/shared/ArchiveGridButton.vue'
import ArchivedGridToggle from '@/components/shared/ArchivedGridToggle.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import DeleteOptionsDialog from '@/components/shared/DeleteOptionsDialog.vue'
import BudgetDocumentRowsGrid from '@/components/accounting/BudgetDocumentRowsGrid.vue'
import BudgetDocumentEdit from '@/components/accounting/BudgetDocumentEdit.vue'
import BudgetDocumentPrintDialog from '@/components/accounting/BudgetDocumentPrintDialog.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'

import { useAccountingStore } from '@/stores/accounting.store'


const gridId = 'budget-documents-grid-v2'

const i18n = getI18n()

const detailGridRef = ref(null)
const dataGridWidget = useDataGridWidget()
const { gridRef } = useGridRef()
const editItem = ref(null)
const quickCreate = ref(false)
const onAddClick = (quick = false) => { quickCreate.value = quick === true; editItem.value = {} }
const { isMobile } = useDetectDevice()
const printItem = ref(null)
const { notifySuccess, notifyWarning } = useNotify()
const { getBudgetDocumentEstimateChip, getBudgetDocumentTagChips } = useChips()
const { onContentReady } = useRouteColumnFilter(gridRef, ['code', 'year_mnemonic', 'number_mnemonic'])

const accountingStore = useAccountingStore()

accountingStore.fetchBudgetTagsAsync({}, true)
let localizedBudgetTags = [...accountingStore.getLocalizedBudgetTags().sort((a, b) => a?.name?.localeCompare(b?.name))]

const gridDataSource = useODataSource(accountingStore.api.getAbsoluteUrl() + accountingStore.api.budgetDocumentsUrl, {
  customParamsFn: () => { return showArchived.value ? { show_archived: true } : {} }
})

const { exportOptions, onExporting } = useDefaultExport({ fileName: i18n.t('common.budgetDocument', 2) })

const { deleteOptions, openDeleteOptions, onDeleteOptionSelect } = useQuoteDeleteOptions({
  onDeleted: async() => { await gridRef.value?.instance.refresh() },
})

const { archiveWarning, archiveButton, showArchived, archiveDisplayExpr, archiveCellTemplate, archiveAsync, onRowPrepared: onArchiveRowPrepared, onRowRemoving } = useArchive({
  onArchive: async(id) => { await accountingStore.archiveBudgetDocumentAsync(id) },
  onDelete: async(id, config) => { await accountingStore.deleteBudgetDocumentAsync(id, config) },
  gridRef: gridRef,
  onDeleteBlocked: (item, error) => { openDeleteOptions(item, error) },
})

const columns = [
  {
    dataField: 'code',
    caption: i18n.t('common.code'),
    width: '6rem',
  },
  {
    type: 'buttons',
    showInColumnChooser: false,
    buttons: [
        { name: 'edit', visible: (e) => !e.row?.data?.archived, onClick: (e) => { editItem.value = { ...e.row.data } } },
        { name: 'delete', visible: (e) => !e.row?.data?.archived },
        archiveButton,
    ],
    fixed: false,
    visibleIndex: -1,
    width: '6rem',
  },
  dataGridColumn.useYearMnemonicColumn(),
  dataGridColumn.useNumberMnemonicColumn(),
  dataGridColumn.useIsEstimateColumn(),
  dataGridColumn.useDateColumn({
    validationRules: [{ type: 'required' }]
  }),
  dataGridColumn.useProjectsColumn(),
  {
    dataField: 'name',
    caption: i18n.t('common.name'),
    validationRules: [{ type: 'required' }],
    width: '12rem',
  },
  dataGridColumn.useTotalNoVatColumn(),
  dataGridColumn.useTotalVatColumn(),
  dataGridColumn.useCostTotalColumn(),
  dataGridColumn.useMarginColumn(),
  dataGridColumn.useMarginPercentageColumn(),
  {
    dataField: 'request_reference',
    caption: i18n.t('common.requestReference'),
  },
  {
    dataField: 'description',
    caption: i18n.t('common.description'),
    visible: false,
    width: '12rem',
  },
  dataGridColumn.useBudgetDocumentTagsColumn({
    dataSource: localizedBudgetTags,
    getTagsFn: () => localizedBudgetTags,
    cellTemplate: 'budget-document-tags-cell-template',
  }),
  dataGridColumn.useListingColumn({archiveDisplayExpr, archiveCellTemplate}),
  dataGridColumn.useCurrencyColumn(),
  dataGridColumn.useStartColumn({
    validationRules: [{
      type: 'custom',
      validationCallback: (e) => {
        return e.data.is_estimate ? true : !!e.value
      },
      message: i18n.t('common.required'),
      reevaluate: true,
    }],
  }),
  dataGridColumn.useEndColumn({
    validationRules: [{
      type: 'custom',
      validationCallback: (e) => {
        return e.data.is_estimate ? true : !!e.value
      },
      message: i18n.t('common.required'),
      reevaluate: true,
    }],
  }),
  {
    dataField: 'intellectual_property_terms',
    caption: i18n.t('common.intellectualPropertyTerms'),
    visible: false,
  },
  {
    dataField: 'budget_estimate_terms',
    caption: i18n.t('common.budgetEstimateTerms'),
    visible: false,
  },
  {
    dataField: 'payment_terms',
    caption: i18n.t('common.paymentTerms'),
    visible: false,
  },
  {
    dataField: 'payment_coordinates',
    caption: i18n.t('common.paymentCoordinates'),
    visible: false,
  },
  {
    dataField: 'notes',
    caption: i18n.t('common.note', 2),
    visible: false,
  },
  dataGridColumn.useArchivedAtColumn({visible: false}),
  dataGridColumn.useArchivedColumn({visible: false}),
]


const onPrintWidgetClick = () => {
  const selectedRows = gridRef.value.instance.getSelectedRowsData()
  if (selectedRows.length === 1) {
    printItem.value = { ...selectedRows[0] }
  }
}

const createProjectFromBudgetWidget = () => dataGridWidget.createProjectWidget({
  onClick: async() => {
    const selectedRows = gridRef.value.instance.getSelectedRowsData()
    if (selectedRows.length === 1) {
      const selectedRow = selectedRows[0]

      if (selectedRow.is_estimate) {
        notifyWarning(i18n.t('warning.cannotCreateProjectFromEstimateDocument'))
        return
      }

      await accountingStore.generateProjectFromBudgetDocumentAsync(selectedRow.id).then(() => {
        notifySuccess(i18n.t('common.projectCreated'))
      })
      router.push(menuRoutes[menuRouteEnum.PROJECTS])
    }
  },
})

const cloneBudgetWidget = () => dataGridWidget.cloneWidget({
  onClick: async() => {
    const selectedRows = gridRef.value.instance.getSelectedRowsData()
    if (selectedRows.length === 1) {
      const selectedRow = selectedRows[0]
      await accountingStore.cloneBudgetDocumentAsync(selectedRow.id).then(() => {
        notifySuccess(i18n.t('common.documentCloned'))
      })
      gridRef.value.instance.refresh()
    }
  },
})



const onSelectionChanged = (e) => {
  dataGridWidget.cloneWidgetButton.value.option('disabled', e.selectedRowsData.length !== 1)
  dataGridWidget.printWidgetButton.value.option('disabled', e.selectedRowsData.length !== 1)
  dataGridWidget.createProjectWidgetButton.value.option('disabled', e.selectedRowsData.length !== 1 || e.selectedRowsData[0].archived)
}

const updateGrid = async (saved) => {
  const createdId = getCreatedRowId(editItem.value, saved)
  editItem.value = null
  await accountingStore.fetchBudgetTagsAsync({}, true)
  localizedBudgetTags = [...accountingStore.getLocalizedBudgetTags().sort((a, b) => a?.name?.localeCompare(b?.name))]
  await refreshGridAsync(gridRef, createdId)
}

const onDetailRefSet = (ref) => {
  detailGridRef.value = ref
}

const gridOptions = useDefaultGridOptions({
  id: gridId,
  columns: columns,
  dataSource: gridDataSource,
  editing: {
    mode: 'popup',
    allowUpdating: true,
    allowAdding: true,
    allowDeleting: true,
    useIcons: true,
  },
  selection: { mode: 'multiple', showCheckBoxesMode: 'onClick', selectAllMode: 'page' },
  export: exportOptions,
  onExporting: onExporting,
  masterDetail: {
    enabled: true,
    template: 'master-detail',
  },
  onContentReady: onContentReady,
  onRowRemoving: onRowRemoving,
  onRowPrepared: onArchiveRowPrepared,
  exportWidgets: [
    createProjectFromBudgetWidget(),
    cloneBudgetWidget(),
    dataGridWidget.printWidget({ onClick: () => onPrintWidgetClick() }),
  ],
  onSelectionChanged: onSelectionChanged,
  clearFilterButton: true,
  archivedToggle: true,
  addButtons: [{
    icon: 'mdi-flash',
    hint: i18n.t('common.quickCreate'),
    onClick: () => onAddClick(true),
  }],
  addButton: { onClick: () => onAddClick(false) },
})

const { repaintRow } = useUpdateMasterDetail()
const onDetailRowSaved = (parentId) => {
  repaintRow(gridRef, accountingStore.api.getBudgetDocumentByIdAsync, parentId, parentId)
}


useEntityChanges([entityChangeTypeEnum.QUOTE], (change) => refreshChangedRowAsync(gridRef, change, accountingStore.api.getBudgetDocumentByIdAsync), () => editItem.value !== null)
</script>
<template>
  <div>
    <q-dialog :model-value="printItem !== null" :maximized="isMobile" @hide="printItem = null">
      <budget-document-print-dialog
        v-if="printItem !== null"
        :budget-document-id="printItem.id"
        @cancel="printItem = null"
      />
    </q-dialog>
    <q-dialog :model-value="editItem !== null" class="dialog-sm" @hide="updateGrid">
      <budget-document-edit
        v-if="editItem !== null"
        :model-value="editItem"
        :initial-quick-create="quickCreate"
        @update="updateGrid"
        @cancel="updateGrid"
        @delete="updateGrid"
      />
    </q-dialog>
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async() => await archiveAsync(archiveWarning.item.id, false)"
    />
    <delete-options-dialog
      v-model="deleteOptions.show"
      :title="deleteOptions.title"
      :message="deleteOptions.message"
      :caption="deleteOptions.caption"
      :options="deleteOptions.options"
      @select="onDeleteOptionSelect"
    />
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #master-detail="{ data: budgetDocument }">
        <budget-document-rows-grid
          :data="budgetDocument"
          :listing="budgetDocument.data.listing"
          @grid-ref-set="onDetailRefSet"
          @row-saved="onDetailRowSaved"
        />
      </template>
      <template #archive-button="{ data: row }">
        <archive-grid-button :row="row" />
      </template>
      <template #is-estimate-cell-template="{ data: templateOptions }">
        <chip-template :values="[getBudgetDocumentEstimateChip(templateOptions.data.is_estimate)]" />
      </template>
      <template #archived-toggle>
        <archived-grid-toggle
          :show-archived="showArchived"
          :grid-ref="gridRef"
          @update:show-archived="showArchived = $event"
        />
      </template>
      <template #budget-document-tags-cell-template="{ data: cellInfo }">
        <chip-template :values="getBudgetDocumentTagChips(cellInfo.data.tag_ids)" />
      </template>
    </dx-data-grid>
  </div>
</template>
